import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { rwApi, setRwToken, formatApiErr } from "@/lib/rwApi";
import { Home, Lock, Users } from "lucide-react";
import { toast } from "sonner";

export default function RwLoginPage() {
  const [nik, setNik] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (nik.length !== 16) {
      setError("Masukkan NIK 16 digit.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await rwApi.post("/rw/login", { nik });
      setRwToken(data.access_token);
      toast.success(`Selamat datang, ${data.officer.nama || "Ketua RW"}`);
      navigate("/rw/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="rw-login-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center font-bold">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight">SIPILKADES · Portal Ketua RW</div>
              <div className="text-xs text-slate-500">Akses data pemilih untuk Ketua RW</div>
            </div>
          </div>
          <a href="/" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="rw-back-home">
            <Home className="h-3.5 w-3.5" /> Beranda
          </a>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2 text-slate-900">
                <Lock className="h-4 w-4" />
                <h1 className="font-display text-xl font-bold">Masuk sebagai Ketua RW</h1>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                Cukup masukkan NIK 16 digit Anda. Sistem akan mengenali RW dan dusun Anda secara otomatis.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">NIK Ketua RW</label>
                <Input
                  value={nik}
                  onChange={(e) => setNik(e.target.value.replace(/\D/g, "").slice(0, 16))}
                  inputMode="numeric"
                  maxLength={16}
                  placeholder="16 digit NIK"
                  className="h-12 font-mono tracking-wider text-base"
                  data-testid="rw-login-nik-input"
                  autoComplete="off"
                />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="rw-login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || nik.length !== 16}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 font-semibold disabled:opacity-50"
                data-testid="rw-login-submit"
              >
                {loading ? "Memverifikasi..." : "Masuk"}
              </Button>
            </form>

            <p className="text-[11px] text-slate-500 text-center">
              Jika NIK Anda belum terdaftar sebagai Ketua RW, hubungi Panitia Pilkades.
            </p>
          </CardContent>
        </Card>
      </main>

      <footer className="text-center text-xs text-slate-500 py-4">
        © 2026 SIPILKADES · Desa Mekarsari
      </footer>
    </div>
  );
}
